// src/components/wordbook/EditWordDialog.tsx
import {FC, useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    Slider,
    Typography,
} from '@mui/material';
import {Save, Close} from '@mui/icons-material';
import {useUpdateWord} from '../../hooks/useWord';
import {UpdateWordRequest} from '../../models/word/wordRequests';
import {WordInfo} from '../../types/wordbook';
import { formatKstDateTime } from '../../utils/dateTime';

interface Props {
    open: boolean;
    onClose: () => void;
    wordId: number;
    wordbookId: number;
    word: WordInfo;
}

export const EditWordDialog: FC<Props> = ({
                                              open,
                                              onClose,
                                              wordId,
                                              wordbookId,
                                              word,
                                          }) => {
    const updateWord = useUpdateWord();

    const [formData, setFormData] = useState<UpdateWordRequest>({
        wordText: word.wordText,
        meaningText: word.meaningText,
        difficultyBase: word.difficultyBase || 50,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        setFormData({
            wordText: word.wordText,
            meaningText: word.meaningText,
            difficultyBase: word.difficultyBase || 50,
        });
    }, [word]);

    const handleChange = <K extends keyof UpdateWordRequest>(field: K, value: UpdateWordRequest[K]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.wordText?.trim()) {
            newErrors.wordText = '단어를 입력해주세요.';
        }
        if (!formData.meaningText?.trim()) {
            newErrors.meaningText = '의미를 입력해주세요.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        updateWord.mutate(
            {
                wordId,
                wordbookId,
                data: formData,
            },
            {
                onSuccess: () => {
                    onClose();
                },
            }
        );
    };

    const handleClose = () => {
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>단어 수정</DialogTitle>
            <DialogContent>
                {updateWord.isError && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {updateWord.error?.message || '단어 수정에 실패했습니다.'}
                    </Alert>
                )}

                <Grid container spacing={2} sx={{mt: 1}}>
                    {/* 단어 */}
                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label={`단어 (${word.wordLang})`}
                            value={formData.wordText || ''}
                            onChange={(e) => handleChange('wordText', e.target.value)}
                            error={!!errors.wordText}
                            helperText={errors.wordText}
                            required
                        />
                    </Grid>

                    {/* 의미 */}
                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label={`의미 (${word.meaningLang})`}
                            value={formData.meaningText || ''}
                            onChange={(e) => handleChange('meaningText', e.target.value)}
                            error={!!errors.meaningText}
                            helperText={errors.meaningText}
                            required
                        />
                    </Grid>

                    {/* 난이도 */}
                    <Grid size={{xs: 12}}>
                        <Box sx={{px: 2}}>
                            <Typography gutterBottom>
                                난이도: {formData.difficultyBase}
                            </Typography>
                            <Slider
                                value={formData.difficultyBase || 50}
                                onChange={(_, value) => handleChange('difficultyBase', value)}
                                min={0}
                                max={100}
                                marks={[
                                    {value: 0, label: '쉬움'},
                                    {value: 50, label: '보통'},
                                    {value: 100, label: '어려움'},
                                ]}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                    </Grid>

                    {/* 추가 정보 표시 */}
                    <Grid size={{xs: 12}}>
                        <Box
                            sx={{
                                bgcolor: 'grey.100',
                                p: 2,
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block">
                                단어 ID: {word.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                사전 ID: {word.dictId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                순서: {word.sortOrder + 1}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                생성일: {formatKstDateTime(word.createdAt)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={updateWord.isPending}>
                    <Close sx={{mr: 0.5}}/>
                    취소
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={updateWord.isPending}
                >
                    <Save sx={{mr: 0.5}}/>
                    {updateWord.isPending ? '저장 중...' : '저장'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
